/**
 * Tours hooks — TanStack Query para catálogo y detalle de experiencias (F1).
 */
import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../supabase'
import { queryClient } from '../query-client'
import { toursService } from '../services/tours.service'
import type {
  TourHostDepartureInput,
  TourHostProfileInput,
  TourHostUnitInput,
  TourCustomQuoteInput,
  TourGuardianShareInput,
  TourTripOperatorAction,
  TourPayInput,
  TourReserveInput,
  TourReviewInput,
  TourSplitPayInput,
} from '../types/tours.types'

export const toursQueryKeys = {
  catalog: (country: string) => ['tours', 'catalog', country] as const,
  experience: (experienceId: string) => ['tours', 'experience', experienceId] as const,
  bookings: ['tours', 'bookings'] as const,
  host: ['tours', 'host'] as const,
  hostPayouts: ['tours', 'host', 'payouts'] as const,
  hostDepartures: ['tours', 'host', 'departures'] as const,
  hostBookings: ['tours', 'host', 'bookings'] as const,
  reviews: (experienceId: string) => ['tours', 'reviews', experienceId] as const,
  trip: (bookingId: string) => ['tours', 'trip', bookingId] as const,
  guardian: (token: string) => ['tours', 'guardian', token] as const,
}

export function useToursCatalog(country: string) {
  return useQuery({
    queryKey: toursQueryKeys.catalog(country),
    queryFn: () => toursService.getCatalog(country),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

export function useTourExperience(experienceId: string) {
  return useQuery({
    queryKey: toursQueryKeys.experience(experienceId),
    queryFn: () => toursService.getExperience(experienceId),
    enabled: experienceId.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

/** Prefetch experience detail on hover/press so the ficha opens without a wait. */
export function prefetchTourExperience(experienceId: string): void {
  if (!experienceId) return
  void queryClient.prefetchQuery({
    queryKey: toursQueryKeys.experience(experienceId),
    queryFn: () => toursService.getExperience(experienceId),
    staleTime: 5 * 60 * 1000,
  })
}

/** Aparta capacidad + crea la reserva (F2). */
export function useReserveTour() {
  return useMutation({
    mutationFn: (input: TourReserveInput) => toursService.reserve(input),
  })
}

/** Price and materialize a private national itinerary, then hydrate its detail cache. */
export function useCreateCustomTourQuote() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TourCustomQuoteInput) => toursService.createCustomQuote(input),
    onSuccess: (quote) => {
      client.removeQueries({ queryKey: toursQueryKeys.experience(quote.experienceId) })
    },
  })
}

/** Crea el cobro por el puerto de pasarelas (F2). */
export function usePayTour() {
  return useMutation({
    mutationFn: (input: TourPayInput) => toursService.pay(input),
  })
}

/** Confirma el cobro después de 3DS / redirect (F2). */
export function useConfirmTour() {
  return useMutation({
    mutationFn: ({ bookingId, transactionId }: { bookingId: string; transactionId: string }) =>
      toursService.confirm(bookingId, transactionId),
  })
}

/** Reservas del usuario (F2). */
export function useTourBookings() {
  return useQuery({
    queryKey: toursQueryKeys.bookings,
    queryFn: () => toursService.listBookings(),
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/** Cancela una reserva pagada (F2). */
export function useRefundTour() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (bookingId: string) => toursService.refundBooking(bookingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.bookings })
    },
  })
}

/** Divide una reserva en partes (F3). */
export function useCreateSplit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      bookingId,
      participantCount,
    }: {
      bookingId: string
      participantCount: number
    }) => toursService.createSplit(bookingId, participantCount),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.bookings })
    },
  })
}

/** Info pública de una parte del split (F3). */
export function useSplitInfo(token: string) {
  return useQuery({
    queryKey: ['tours', 'split', token] as const,
    queryFn: () => toursService.getSplitInfo(token),
    enabled: token.length > 0,
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== 'settled' ? 5_000 : false,
    retry: false,
  })
}

/** Paga una parte del split (F3). */
export function usePaySplit() {
  return useMutation({
    mutationFn: (input: TourSplitPayInput) => toursService.paySplit(input),
  })
}

/** Perfil del host + unidades (F3). */
export function useTourHost(enabled = true) {
  return useQuery({
    queryKey: toursQueryKeys.host,
    queryFn: () => toursService.getHost(),
    enabled,
    staleTime: 30 * 1000,
  })
}

/** Alta/edición del perfil del host (F3). */
export function useSaveTourHost() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TourHostProfileInput) => toursService.saveHost(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.host })
    },
  })
}

/** Alta/edición de una unidad (F3). */
export function useSaveTourHostUnit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TourHostUnitInput) => toursService.saveHostUnit(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.host })
    },
  })
}

/** Ganancias y payouts del host (F3). */
export function useTourHostPayouts(enabled = true) {
  return useQuery({
    queryKey: toursQueryKeys.hostPayouts,
    queryFn: () => toursService.getHostPayouts(),
    enabled,
    staleTime: 30 * 1000,
  })
}

/** Reseñas de una experiencia (F4). */
export function useTourReviews(experienceId: string) {
  return useQuery({
    queryKey: toursQueryKeys.reviews(experienceId),
    queryFn: () => toursService.listReviews(experienceId),
    enabled: experienceId.length > 0,
    staleTime: 60 * 1000,
  })
}

/** Deja una reseña (F4). */
export function useSubmitTourReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TourReviewInput) => toursService.submitTourReview(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tours', 'reviews'] })
      void queryClient.invalidateQueries({ queryKey: ['tours', 'bookings'] })
    },
  })
}

/** Experiencias + salidas del host (F3). */
export function useTourHostDepartures(enabled = true) {
  return useQuery({
    queryKey: toursQueryKeys.hostDepartures,
    queryFn: () => toursService.getHostDepartures(),
    enabled,
    staleTime: 30 * 1000,
  })
}

/** Crea una salida (F3). */
export function useCreateHostDeparture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TourHostDepartureInput) => toursService.createHostDeparture(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.hostDepartures })
    },
  })
}

/** Reservas recibidas por el host (F3). */
export function useTourHostBookings(enabled = true) {
  return useQuery({
    queryKey: toursQueryKeys.hostBookings,
    queryFn: () => toursService.getHostBookings(),
    enabled,
    staleTime: 30 * 1000,
  })
}

/** Avanza el estado de una reserva del host (F3). */
export function useAdvanceHostBooking() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ bookingId, action }: { bookingId: string; action: 'start' | 'complete' }) =>
      toursService.advanceHostBooking(bookingId, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.hostBookings })
    },
  })
}

/** Operación del viaje con invalidación en tiempo real para estado y ubicación. */
export function useTourTrip(bookingId: string) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: toursQueryKeys.trip(bookingId),
    queryFn: () => toursService.getTrip(bookingId),
    enabled: bookingId.length > 0,
    staleTime: 10 * 1000,
  })
  const tripRunId = query.data?.run?.id ?? null

  useEffect(() => {
    if (!bookingId || !tripRunId) return undefined
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.trip(bookingId) })
    }
    const channel = supabase
      .channel(`tour-trip:${tripRunId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tours_trip_runs',
          filter: `id=eq.${tripRunId}`,
        },
        invalidate,
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tours_trip_locations',
          filter: `trip_run_id=eq.${tripRunId}`,
        },
        invalidate,
      )
      .subscribe()
    return () => {
      void supabase.removeChannel(channel)
    }
  }, [bookingId, queryClient, tripRunId])

  return query
}

export function useTourTripAction(bookingId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      action,
      payload,
    }: {
      action: TourTripOperatorAction | 'trigger_emergency' | 'advance_stop' | 'update_location'
      payload?: Record<string, unknown>
    }) => toursService.runTripAction(bookingId, action, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.trip(bookingId) })
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.bookings })
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.hostBookings })
    },
  })
}

export function useCreateGuardianShare() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TourGuardianShareInput) => toursService.createGuardianShare(input),
    onSuccess: (_, input) => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.trip(input.bookingId) })
    },
  })
}

export function useRevokeGuardianShare(bookingId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => toursService.revokeGuardianShare(bookingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: toursQueryKeys.trip(bookingId) })
    },
  })
}

/** Los enlaces Guardian no abren sesión; polling acotado mantiene el mapa vivo. */
export function useTourGuardian(token: string) {
  return useQuery({
    queryKey: toursQueryKeys.guardian(token),
    queryFn: () => toursService.getGuardianTrip(token),
    enabled: token.length === 64,
    staleTime: 10 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status
      return status && !['completed', 'cancelled'].includes(status) ? 15_000 : false
    },
    retry: false,
  })
}
